import getArtStoreApi from './artStoreApi';
import {
  ARTSTORE_PUBSUB_ENUMS,
  ART_STORE,
  cartItemsStatuses,
  SECTIONS_ID,
} from '../../constants';
import wixAppsApi from './wixAppsApi';
import ProGalleryStore from '../../common/ProGalleryStore';
import { translationUtils } from '@wix/photography-client-lib';
import CartApi from './cartStorageApi';

class Deferred {
  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.reject = reject;
      this.resolve = resolve;
    });
  }
}

export default class ArtStoreManager {
  constructor(context) {
    this.context = context;
    this.pagesState = {
      showCheckout: false,
      showCart: false,
      showShopEditor: false,
      showStorePopupMessage: false,
    };
    this.cartStorageKey = 'artStoreCartCount';
    this.activeStoreWidget = null;
    this.activeWidgets = [];
    this.cartApi = new CartApi();
    this.appDefId = ART_STORE.ARTSTORE_APP_DEFINITION_ID;
    this.additionalProviderParams = null;
  }

  shouldSkipItems = (wixCodeApi) => {
    return !!wixCodeApi.location.query.skipItems;
  };

  initCartApiIfNeeded(isSSR = false) {
    // in SSR we cant access localStorage, to avoid errors use default cartApi.
    if (isSSR) {
      this.cartApi = {
        isSsrMock: true,
        initIfNeeded: () => {},
        clearAll: () => {},
        addToCart: () => {},
        updateCartItem: () => {},
        removeCartItem: () => {},
        getCartItems: () => [],
        getCartItemsCount: () => 0,
      };
    } else if (this.cartApi && this.cartApi.isSsrMock) {
      this.cartApi = new CartApi();
    }
    const context = this.context.getContext();
    this.cartApi.initIfNeeded(context);
  }

  setActiveWidgetIfNeeded(activeWidgets) {
    if (!this.activeWidgets.length) {
      this.activeWidgets = activeWidgets;
    }
    if (!this.activeStoreWidget) {
      const galleryWidget = this.activeWidgets.find(
        (widget) => widget.type === ART_STORE.GALLERY_WIDGET_ID,
      );
      const cartWidget = this.activeWidgets.find(
        (widget) => widget.type === ART_STORE.CART_WIDGET_ID,
      );
      if (galleryWidget || cartWidget) {
        this.activeStoreWidget = galleryWidget || cartWidget;
      }
    }
  }

  getDeferredPromise() {
    return new Deferred();
  }

  getConsentPolicy(wixCodeApi) {
    try {
      const { query } = wixCodeApi.location;
      if (query && query['consent-policy']) {
        const consentPolicy = JSON.parse(
          decodeURIComponent(query['consent-policy']),
        );
        if (consentPolicy.policy) {
          return consentPolicy;
        }
      }
    } catch (e) {}
    return wixCodeApi.user.getCurrentConsentPolicy();
  }

  changeActiveStoreWidget(widget) {
    if (widget.compId !== this.activeStoreWidget.compId) {
      this.activeStoreWidget = widget;
    }
  }

  openCart() {
    if (this.activeStoreWidget && !this.pagesState.showCart) {
      this.pagesState.showCart = true;
      this.activeStoreWidget.setProps({ pagesState: this.pagesState });
    }
  }

  getSectionsUrl() {
    const context = this.context.getContext();
    const getSectionUrl = (sectionId) => {
      const appDefinitionId = ART_STORE.ARTSTORE_APP_DEFINITION_ID;
      return context.scopedGlobalSdkApis.site.getSectionUrl({
        sectionId,
        appDefinitionId,
      });
    };
    return {
      thank_you_page: getSectionUrl(SECTIONS_ID.THANK_YOU),
      checkout_page: getSectionUrl(SECTIONS_ID.CHECKOUT),
    };
  }

  getAdditionalProviderParams(
    wixCodeData,
    appDefId = ART_STORE.ARTSTORE_APP_DEFINITION_ID,
  ) {
    const appApi = wixAppsApi[appDefId];
    return appApi.getAdditionalParams(wixCodeData);
  }

  getInstanceFromSettings(
    settings,
    appDefId = ART_STORE.ARTSTORE_APP_DEFINITION_ID,
  ) {
    const appApi = wixAppsApi[appDefId];
    return appApi.getInstanceFromSettings(settings);
  }

  isAlbums(sp) {
    return !!(sp.booleans && sp.booleans.ignoreSavedConnectedProviders);
  }

  async getConnectedProviders(
    instance,
    styleParams, // TODO: remove
    appDefId = ART_STORE.ARTSTORE_APP_DEFINITION_ID,
  ) {
    if (
      this.isAlbums(styleParams) &&
      appDefId === ART_STORE.ARTSTORE_APP_DEFINITION_ID
    ) {
      return Promise.resolve(null);
    }
    // end of remove
    const baseUrl = this.getBaseUrl();
    const appApi = wixAppsApi[appDefId];
    const connectedProviders = await appApi.getConnectedProviders(
      instance,
      baseUrl,
    );
    return Promise.resolve(connectedProviders);
  }

  getLocale() {
    const { scopedGlobalSdkApis } = this.context.getContext();
    if (
      scopedGlobalSdkApis &&
      scopedGlobalSdkApis.site &&
      scopedGlobalSdkApis.site.language
    ) {
      return scopedGlobalSdkApis.site.language;
    } else {
      return 'en';
    }
  }

  initTranslations(setProps) {
    const locale = this.getLocale();
    const translationsPromise = ProGalleryStore.getTranslations(locale);
    translationsPromise.then((translationsRaw) => {
      translationUtils.setTranslations(translationsRaw);
      setProps({ translations: translationsRaw });
    });
  }

  getChangeStoreFrameStateFunc(setProps) {
    return (frame, newFrameState) => {
      const pagesState = this.pagesState;
      switch (frame) {
        case ARTSTORE_PUBSUB_ENUMS.CART:
          if (newFrameState) {
            this.openCart();
            return;
          }
          pagesState.showCart = newFrameState;
          break;
        case ARTSTORE_PUBSUB_ENUMS.CHECKOUT:
          if (newFrameState) {
            pagesState.showCart = false;
            pagesState.showShopEditor = false;
            pagesState.showStorePopupMessage = false;
          }
          pagesState.showCheckout = newFrameState;
          break;
        case ARTSTORE_PUBSUB_ENUMS.EDITOR:
          if (newFrameState) {
            pagesState.showCheckout = false;
            pagesState.showCart = false;
            pagesState.showStorePopupMessage = false;
          }
          pagesState.showShopEditor = newFrameState;
          break;
        case ARTSTORE_PUBSUB_ENUMS.STORE_POPUP_MESSAGE:
          if (newFrameState) {
            pagesState.showCheckout = false;
            pagesState.showCart = false;
            pagesState.showShopEditor = false;
          }
          pagesState.showStorePopupMessage = newFrameState;
          break;
        default:
          break;
      }
      this.pagesState = pagesState;
      setProps({ pagesState });
    };
  }

  getInitalProps(
    { compId, setProps, type, consentPolicy },
    connectedProviders,
    appDefId,
    additionalProviderParams = {},
  ) {
    const baseSectionsUrl = this.getSectionsUrl();
    const sectionsUrls = additionalProviderParams.freeArtStore
      ? {}
      : wixAppsApi[appDefId].getSectionsUrlWithParams(
          baseSectionsUrl,
          additionalProviderParams,
          connectedProviders,
        );
    const { platformServices } = this.context.getContext();
    return {
      changeStoreFrameState: this.getChangeStoreFrameStateFunc(setProps),
      connectedProviders,
      consentPolicy,
      additionalProviderParams,
      pagesState: {
        showCheckout: false,
        showCart: false,
        showShopEditor: false,
        showStorePopupMessage: false,
      },
      setActiveStoreWidget: () =>
        this.changeActiveStoreWidget({ compId, setProps, type }),
      visitorId: platformServices.bi.visitorId,
      metaSiteId: platformServices.bi.metaSiteId,
      cartItemsData: {
        cartItems: this.cartApi.getCartItems(),
        cartItemsCount: this.cartApi.getCartItemsCount(),
      },
      sectionsUrls,
      locale: this.getLocale(),
      ready: true,
      baseUrl: this.getBaseUrl(),
    };
  }

  getBaseUrl() {
    const baseUrl = this.context.getContext().scopedGlobalSdkApis.location
      .baseUrl;
    const baseUrlParts = baseUrl.split('/');
    return baseUrlParts.slice(0, 3).join('/');
  }

  async initStoreWixCodeApi(settings, sp, appDefId) {
    const instance = this.getInstanceFromSettings(settings, appDefId);
    const additionalProviderParams = this.getAdditionalProviderParams(
      settings,
      appDefId,
    );
    this.additionalProviderParams = additionalProviderParams;
    this.appDefId = appDefId;
    if (additionalProviderParams && additionalProviderParams.freeArtStore) {
      return Promise.resolve({ additionalProviderParams });
    }
    return this.getConnectedProviders(instance, sp, appDefId).then(
      (connectedProviders) => {
        return { connectedProviders, additionalProviderParams };
      },
    );
  }

  async updateItemValidation(item) {
    const items = this.cartApi.getCartItems();
    const mediaUrl = item.mediaUrl || item.itemUrl;
    if (mediaUrl) {
      let invalidItems;
      try {
        const appsApi = wixAppsApi[this.appDefId];
        const params =
          this.additionalProviderParams || this.context.getContext();
        invalidItems = await appsApi.getInvalidItemsForDownload(
          params,
          mediaUrl,
          this.getBaseUrl(),
        );
      } catch (e) {
        console.error(e);
        return;
      }
      if (invalidItems && invalidItems.length) {
        invalidItems.forEach((invalidItem) => {
          const _mediaUrl = invalidItem.mediaUrl;
          const result = [];
          Object.keys(items).forEach((uuid) => {
            if (_mediaUrl === items[uuid].mediaUrl) {
              result.push(items[uuid]);
            }
          });
          result.forEach((_item) =>
            this.cartApi.setItemStatus(_item.uuid, cartItemsStatuses.INVALID),
          );
        });
        this.onCartItemsChange();
      }
    }
    return;
  }

  onCartItemsChange() {
    const cartItems = this.cartApi.getCartItems();
    const cartItemsCount = this.cartApi.getCartItemsCount();
    this.activeWidgets.forEach(({ setProps }) => {
      setProps({
        cartItemsData: { cartItems, cartItemsCount },
      });
    });
  }

  navigateToHomePage(wixCodeApi) {
    const siteStructure = wixCodeApi.site.getSiteStructure();
    const homePage = siteStructure.pages.find((page) => page.isHomePage);
    if (homePage && homePage.id) {
      wixCodeApi.location.navigateTo({ pageId: homePage.id });
    }
  }

  getStoreApi(wixCodeApi) {
    return getArtStoreApi(
      () => this.onCartItemsChange(),
      this.cartApi,
      () => this.openCart(),
      (item) => this.updateItemValidation(item),
      () => this.navigateToHomePage(wixCodeApi),
    );
  }
}
